/**
 * @module cercaliagl/view/VWidgetPois
 */

import $ from 'jquery';
import { MDCTabBar } from '@material/tab-bar';
import { MDCSlider } from '@material/slider';
import BaseView, { EventType as BaseViewEventType } from './BaseView';
import {
  createCheckbox, createTabBar, createRadiobox, createSlider,
} from './VUtils';
import { i18n } from '../dictionary';
import { createUid, isMobile } from '../util';

/**
 * @typedef {Object} PoisSearchOptions
 * @property {Array.<string>} categories Categories
 * @property {number} distance Distance
 * @property {number} numCand Num candidates
 * @property {string} searchType Type search. By address or by map center.
 * @property {import("../servers.js").GeEntity} [ge] Geo entity
 */

/**
 * @enum {string}
 */
export const EventType = {
  CATEGORIES_CHANGED: 'categories_changed',
  CLOSE_POIS: 'pois_close',
  POIS_SEARCH: 'pois_search',
  SEARCH_TYPE_CHANGED: 'searchtype_changed',
  MINIMIZE: 'minimize',
};

/**
 * @enum {string}
 */
export const Mode = {
  VIEW_MAP: 'view_map',
  PROXIMITY: 'proximity',
};

/**
 * @enum {string}
 */
export const SearchType = {
  ADDRESS: 'address',
  MAP_CENTER: 'map_center',
};

/**
 * @enum {number}
 */
const DistanceValues = {
  MIN: 1,
  MAX: 50,
  DEFAULT: 25,
};

/**
 * @enum {number}
 */
const CandidatesValues = {
  MIN: 1,
  MAX: 300,
  DEFAULT: 100,
};

/**
 * @enum {number}
 */
const Tabs = {
  VIEW_MAP: 0,
  PROXIMITY: 1,
};

/**
 * @classdesc
 */
class VWidgetPois extends BaseView {
  /**
   * @constructor
   */
  constructor() {
    const jqElement = $(`
      <div class="v-widgetpois mdc-elevation--z4">
        <div class="header">
          ${createTabBar([i18n('viewOnMap'), i18n('proximity')])}
          <div class="btn-close">
            <button type="button" class="mdc-icon-button close">
              <span class="mdc-icon-button__icon icon-close"></span>
            </button>
          </div>
        </div>
        <div class="content">
          <div class="prox-inputs">
            <div class="row radio-typesearch">
              <div class="column">
                ${createRadiobox('typeSearch', SearchType.ADDRESS, true, i18n('searchAddress'), true)}
              </div>
              <div class="column">
                ${createRadiobox('typeSearch', SearchType.MAP_CENTER, true, i18n('mapCenter'), false)}
              </div>
            </div>
            <div class="text-center">
              <div id="poisAutocomplete${createUid()}" class="autocomplete"></div>
            </div>
            <div class="dist-box">
              <div class="slider-title">${i18n('distance')}</div>
              ${createSlider('distance', DistanceValues.MIN, DistanceValues.MAX, DistanceValues.DEFAULT, true, 'Km')}
            </div>
            <div class="numCand-box">
              <div class="slider-title">${i18n('searchCandidates')}</div>
              ${createSlider('numCand', CandidatesValues.MIN, CandidatesValues.MAX, CandidatesValues.DEFAULT, true, ' ')}
            </div>
            <div class="text-right">
              <button type="button" class="mdc-button refresh">
                <span class="mdc-button__ripple"></span>
                <span class="mdc-button__label">${i18n('searchSearchBtn')}</span>
              </button>
            </div>
          </div>
          <div class="cat-cont">
          </div>
        </div>
      </div>
    `);

    super(jqElement);

    /**
     * @private
     * @type {JQuery}
     */
    this.jqProxInputs_ = this.jqElement_.find('.prox-inputs');

    /**
     * @private
     * @type {JQuery}
     */
    this.jqCategoriesDiv_ = this.jqElement_.find('.cat-cont');

    /**
     * @private
     * @type {number}
     */
    this.currentTab_ = Tabs.VIEW_MAP;

    /**
     * @private
     * @type {MDCSlider}
     */
    this.mdcSliderDist_ = null;

    /**
     * @private
     * @type {MDCSlider}
     */
    this.mdcSliderNumCand_ = null;

    /**
     * @private
     * @type {Array.<string>}
     */
    this.categories_ = [];

    /**
     * @private
     * @type {import("../servers.js").GeEntity}
     */
    this.ge_ = null;

    /**
     * @private
     * @type {PoisSearchOptions}
     */
    this.searchOptions_ = {
      categories: [],
      distance: DistanceValues.DEFAULT * 1000,
      numCand: CandidatesValues.DEFAULT,
      searchType: SearchType.ADDRESS,
      ge: null,
    };

    this.initialize_();
  }

  /**
   * @private
   */
  initialize_() {
    if (!this.jqElement_) {
      return;
    }
    const jqButtonClose = this.jqElement_.find('button.close');
    const jqButtonRefresh = this.jqElement_.find('button.refresh');
    if (!jqButtonClose || !jqButtonRefresh) {
      return;
    }
    jqButtonClose.click(() => this.emit(EventType.CLOSE_POIS));
    jqButtonRefresh.click(() => {
      this.searchOptions_.ge = this.searchOptions_.searchType === SearchType.ADDRESS ? this.ge_ : undefined;
      this.emit(EventType.POIS_SEARCH, this.searchOptions_);
    });

    this.on(BaseViewEventType.ATTACHED, () => {
      if (!this.jqElement_) {
        return;
      }
      const jqTabBar = this.jqElement_.find('.mdc-tab-bar');
      if (!jqTabBar || jqTabBar.length === 0) {
        return;
      }
      this.adjustByDevice();
      const tabBar = new MDCTabBar(jqTabBar.get(0));
      tabBar.activateTab(0);
      tabBar.listen('MDCTabBar:activated', (e) => {
        this.currentTab_ = e.detail.index;
        if (this.currentTab_ === Tabs.VIEW_MAP) {
          this.jqProxInputs_.hide();
        } else if (this.currentTab_ === Tabs.PROXIMITY) {
          this.jqProxInputs_.show();
          if (!this.mdcSliderDist_) {
            this.mdcSliderDist_ = new MDCSlider(this.jqElement_.find('.distance').get(0));
            this.mdcSliderDist_.listen('MDCSlider:change', () => {
              const dist = this.mdcSliderDist_.getValue();
              this.jqElement_.find('.distance .value').text(`${dist}Km`);
              this.searchOptions_.distance = dist * 1000;
            });
          }
          if (!this.mdcSliderNumCand_) {
            this.mdcSliderNumCand_ = new MDCSlider(this.jqElement_.find('.numCand').get(0));
            this.mdcSliderNumCand_.listen('MDCSlider:change', () => {
              const numCand = this.mdcSliderNumCand_.getValue();
              this.jqElement_.find('.numCand .value').text(`${numCand}`);
              this.searchOptions_.numCand = numCand;
            });
          }
        }
      });

      this.jqElement_.find('input[name=typeSearch]').change((e) => {
        const searchType = e.target.value;
        this.searchOptions_.searchType = searchType;
        this.emit(EventType.SEARCH_TYPE_CHANGED, searchType);
      });
    });
  }

  /**
   * Adjust by device
   */
  adjustByDevice() {
    const viewport = this.getMapViewport();
    this.jqElement_.css('width', !isMobile() ? 'auto' : viewport.offsetWidth - 10);
    const mapHeight = viewport.offsetHeight;
    this.jqElement_.find('.content').css('maxHeight', mapHeight - 70);
  }

  /**
   * @param {import("../servers.js").GeEntity} ge GeEntity
   */
  setGeoEntity(ge) {
    this.ge_ = ge;
  }

  /**
   * @return {Element} Element
   */
  getAutocomplete() {
    return this.jqElement_.find('div.autocomplete').get(0);
  }

  /**
   * @param {Array.<Object>} cats Categories from JSON
   */
  setCategories(cats) {
    cats.forEach((cat) => {
      if (cat.id === 'traffic') return;
      const jqCatDiv = $(`
        <div class="groupcat ${cat.id}">
          <div class="btn-collapse">${cat.name}</div>
          <ul class="collapse ${cat.id}">
            ${cat.subcats.map((subcat) => `<li>${createCheckbox(subcat.name, subcat.id, `<span class="icon icon-${subcat.id.replace('|', '')}"></span><span class="text">${subcat.name}</span>`, false)}</li>`).join('')}
          </ul>
        </div>`)
        .appendTo(this.jqCategoriesDiv_);
      jqCatDiv.find('div.btn-collapse').click((e) => {
        this.jqCategoriesDiv_.find('ul.collapse').each((index, elem) => {
          if (e.currentTarget.nextElementSibling === elem) {
            $(e.currentTarget).next().toggle();
          } else {
            $(elem).hide();
          }
        });
      });
    });

    this.jqCategoriesDiv_.find('li input[type=checkbox]').change((e) => {
      const checkbox = e.currentTarget;
      const subcat = checkbox.value;
      if (checkbox.checked) {
        this.categories_.push(subcat);
      } else {
        const index = this.categories_.indexOf(subcat);
        this.categories_.splice(index, 1);
      }
      this.searchOptions_.categories = this.categories_;
      if (this.currentTab_ === Tabs.VIEW_MAP) {
        this.emit(EventType.CATEGORIES_CHANGED, this.categories_);
      }
    });
  }

  /**
   * Uncheck all
   */
  uncheckAll() {
    this.jqCategoriesDiv_.find('li input[type=checkbox]').prop('checked', false);
    this.searchOptions_.categories = [];
    this.emit(EventType.CATEGORIES_CHANGED, []);
  }
}

export default VWidgetPois;
